import * as React from "react";

import { ErrorPanel } from "./ErrorPanel";
import { isStaleBuildError, reloadForStaleBuild, StaleBuildNotice } from "./StaleBuild";

/**
 * A render-time crash barrier.
 *
 * React only supports error boundaries as class components, so this is the one
 * class in the tree. The fallback receives the real error and a reset callback;
 * it never renders a fabricated "everything is fine" state in place of a page
 * that failed to render.
 */
export interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** What this subtree was showing, e.g. "the posture screen". */
  label?: string;
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.props.onError?.(error, info);

    // A page whose chunk is gone cannot be retried — the file is not coming
    // back — so the boundary reloads once instead of offering a Try again
    // button that can only fail. The guard inside makes this at most one
    // attempt per tab; if it returns false the fallback below explains it.
    if (isStaleBuildError(error)) reloadForStaleBuild();
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback(error, this.reset);
    // Not an application failure, so it does not get the failure panel: the
    // deployment moved on while this tab was open, and "Try again" would
    // re-import the same missing chunk.
    if (isStaleBuildError(error)) return <StaleBuildNotice error={error} />;
    return (
      <ErrorPanel
        action={`render ${this.props.label ?? "this screen"}`}
        error={error}
        onRetry={this.reset}
      />
    );
  }
}
